import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Save,
  ArrowLeft,
  Loader2,
  Info,
  Sparkles,
  GitBranch,
  TestTube,
  Clock,
  Code,
  Zap,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { WorkflowCanvas } from './WorkflowCanvas';
import { WorkflowActivityTracker } from './WorkflowActivityTracker';
import { AICopilotPanel } from './AICopilotPanel';
import { WorkflowVersioning } from './WorkflowVersioning';
import { WorkflowTestingPanel } from './WorkflowTestingPanel';
import { WorkflowScheduler } from './WorkflowScheduler';
import { DataTransformerPanel } from './DataTransformerPanel';
import { ExecutionTimeline } from './ExecutionTimeline';
import { Workflow } from '@shared/types';

export function WorkflowBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workflowName, setWorkflowName] = useState(id ? `Workflow ${id}` : '');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [trigger, setTrigger] = useState('manual');
  const [isSaving, setIsSaving] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(true);
  const [activeTab, setActiveTab] = useState('canvas');

  // Stub workflow object for sub-panels that require a Workflow instance
  const workflowStub: Workflow = {
    id: id || '',
    name: workflowName,
    description: workflowDescription,
    status: 'DRAFT' as any,
    trigger: 'MANUAL' as any,
    nodes: [],
    createdBy: '',
    version: 1,
    tags: [],
    createdAt: new Date(),
    updatedAt: new Date(),
    runCount: 0,
  };

  const saveWorkflow = async () => {
    if (!workflowName) {
      toast.error('Por favor ingresa un nombre para el workflow');
      return;
    }

    setIsSaving(true);
    toast.info('Guardando workflow...');
    
    // Simular guardado
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSaving(false);
    toast.success(`Workflow "${workflowName}" guardado como borrador`);
    toast.info('Puedes enviarlo a revisión desde la lista de workflows');
    
    // Navegar a la lista de workflows después de 1 segundo
    setTimeout(() => {
      navigate('/dashboard/workflows');
    }, 1500);
  };

  const handleCancel = () => {
    if (workflowName) {
      setCancelDialogOpen(true);
    } else {
      navigate('/dashboard/workflows');
    }
  };

  const confirmCancel = () => {
    toast.info('Cambios descartados');
    navigate('/dashboard/workflows');
  };

  return (
    <div className="space-y-6 p-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleCancel}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl mb-2">
              {id ? 'Editar Workflow' : 'Crear Workflow'}
            </h1>
            <p className="text-muted-foreground">
              Construye tu automatización arrastrando nodos al canvas
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button 
            onClick={saveWorkflow}
            className="bg-primary hover:bg-primary/90"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Guardar Workflow
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Info banner */}
      {showInfo && (
        <Card className="relative overflow-hidden border-blue-500/50 bg-blue-500/5">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500"></div>
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-sm mb-1">💡 Cómo usar el Workflow Builder</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Explora la <strong>Paleta de Nodos</strong> (izquierda) - tenemos 40+ nodos organizados en 6 categorías</li>
                  <li>• Haz clic en cualquier nodo para agregarlo al canvas</li>
                  <li>• Selecciona un nodo para configurar sus parámetros en el panel derecho</li>
                  <li>• Los nodos se conectan automáticamente - o haz clic para conectar manualmente</li>
                  <li>• Haz clic en "Ejecutar Workflow" para ver tu automatización en acción en la consola</li>
                </ul>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowInfo(false)}
                className="flex-shrink-0"
              >
                Cerrar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workflow Info */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          <CardHeader>
            <CardTitle className="text-base">Información del Workflow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                placeholder="Ej: Envío automático de ofertas"
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                className="h-10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                placeholder="Describe qué hace este workflow..."
                value={workflowDescription}
                onChange={(e) => setWorkflowDescription(e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border lg:col-span-2">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          <CardHeader>
            <CardTitle className="text-base">Resumen</CardTitle>
            <CardDescription className="text-sm">
              Este workflow ejecutará una serie de nodos conectados para automatizar tareas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-accent/50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Categorías disponibles</p>
                <p className="text-2xl font-semibold">6</p>
                <p className="text-xs text-muted-foreground mt-1">Triggers, Acciones, Lógica, Datos, Integraciones, AI</p>
              </div>
              <div className="bg-accent/50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Total de nodos</p>
                <p className="text-2xl font-semibold">40+</p>
                <p className="text-xs text-muted-foreground mt-1">Opciones para cualquier automatización</p>
              </div>
              <div className="bg-accent/50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Estado</p>
                <p className="text-2xl font-semibold">✨</p>
                <p className="text-xs text-muted-foreground mt-1">Listo para construir</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Canvas */}
      <WorkflowCanvas workflowId={id} />

      {/* Activity Tracker */}
      <WorkflowActivityTracker workflowId={id || ''} workflowName={workflowName} />

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar cambios</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que quieres salir sin guardar? 
              Se perderán todos los cambios realizados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={confirmCancel}
            >
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tabs */}
      <Tabs defaultValue={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full">
          <TabsTrigger value="canvas">Canvas</TabsTrigger>
          <TabsTrigger value="ai">AI Copilot</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="versioning">Versiones</TabsTrigger>
          <TabsTrigger value="testing">Pruebas</TabsTrigger>
          <TabsTrigger value="scheduler">Programador</TabsTrigger>
          <TabsTrigger value="data">Transformador</TabsTrigger>
        </TabsList>
        <TabsContent value="canvas">
          <WorkflowCanvas workflowId={id} />
        </TabsContent>
        <TabsContent value="ai">
          <div className="h-[600px] border rounded-xl overflow-hidden">
            <AICopilotPanel workflowId={id} />
          </div>
        </TabsContent>
        <TabsContent value="timeline">
          <ExecutionTimeline workflowId={id} limit={30} />
        </TabsContent>
        <TabsContent value="versioning">
          <WorkflowVersioning workflow={workflowStub} />
        </TabsContent>
        <TabsContent value="testing">
          <WorkflowTestingPanel workflow={workflowStub} />
        </TabsContent>
        <TabsContent value="scheduler">
          <WorkflowScheduler workflowId={id} />
        </TabsContent>
        <TabsContent value="data">
          <DataTransformerPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}